import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, FormControlLabel, Switch, Box, Typography } from '@mui/material';

import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Input from '@/components/Input/Input';
import { EditPatchTypeHeaderProps } from './EditPatchTypeHeader.types';

import styles from './EditPatchTypeHeader.module.css';

export const EditPatchTypeHeader = ({
  bundleUpchargeSelected,
  descriptionAlreadyExists,
  onCancel,
  onToggleLock,
  onSave,
  onHandleDescriptionChange,
}: EditPatchTypeHeaderProps) => {
  const { t } = useTranslation();
  const description = bundleUpchargeSelected?.description;
  const [error, setError] = useState('');

  const errorMessage = useMemo(() => {
    if (descriptionAlreadyExists) {
      return t('bundleUpcharges.editPatchTypeHeader.descriptionAlreadyExists');
    }

    if (!description || !description.trim()) {
      return t('bundleUpcharges.editPatchTypeHeader.descriptionRequired');
    }
    return '';
  }, [descriptionAlreadyExists, description, t]);

  const handleSave = useCallback(() => {
    if (!description || !description.trim()) {
      setError(t('bundleUpcharges.editPatchTypeHeader.descriptionRequired'));
      return;
    }
    setError('');
    onSave();
  }, [description, onSave, t]);

  return (
    <Box
      data-testid="edit-patch-type-header"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      width="100%"
    >
      <Box display="flex" alignItems="center" gap={4}>
        <Box>
          <Input
            value={description}
            maxLength={50}
            id="description"
            label={t('bundleUpcharges.editPatchTypeHeader.inputLabel')}
            onChange={onHandleDescriptionChange}
            className={!description || descriptionAlreadyExists ? styles.inputDescription : ''}
            autoComplete="off"
          />
          {(errorMessage || error) && (
            <Typography fontSize="1.5rem" color="var(--red-700)">
              {errorMessage || error}
            </Typography>
          )}
        </Box>
        <Box mt={2}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  color="error"
                  title="switch"
                  checked={bundleUpchargeSelected?.isLocked}
                  onClick={onToggleLock}
                />
              }
              label={
                <Typography fontSize="1.75rem" fontFamily="Open Sans" minWidth="fit-content">
                  {t('bundleUpcharges.editPatchTypeHeader.switchLabel')}
                </Typography>
              }
            />
          </FormGroup>
        </Box>
      </Box>

      <Box display="flex" alignItems="center" gap={2}>
        <Button variant={ButtonVariants.Text} onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant={ButtonVariants.Primary} onClick={handleSave}>
          {t('common.save')}
        </Button>
      </Box>
    </Box>
  );
};
